import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { validateImageFile, generateSafeFilename } from "@/lib/fileValidation";
import { optimizeImage } from "@/lib/imageOptimizer";
import { useState } from "react";

export interface GalleryImage {
  id: string;
  image_url: string;
  alt_text: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useGalleryImages = () => {
  return useQuery({
    queryKey: ["gallery-images"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_images")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as GalleryImage[];
    },
  });
};

export const useAdminGalleryImages = () => {
  return useQuery({
    queryKey: ["admin-gallery-images"],
    queryFn: async () => {
      // Admin query that includes inactive images
      const { data, error } = await supabase
        .from("gallery_images")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as GalleryImage[];
    },
  });
};

export const useAddGalleryImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { file: File; altText: string }) => {
      const { file, altText } = params;
      
      // Validate file type and size
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error || "Invalid file");
      }
      
      // Optimize image before upload (resize, convert to WebP, compress)
      const optimizedFile = await optimizeImage(file);
      
      // Use the optimized file's extension (webp or jpg fallback)
      const ext = optimizedFile.name.split(".").pop() || "webp";
      const fileName = generateSafeFilename("gallery", ext);
      
      const { error: uploadError } = await supabase.storage
        .from("gallery")
        .upload(fileName, optimizedFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("gallery")
        .getPublicUrl(fileName);

      // Get current max display_order
      const { data: existing } = await supabase
        .from("gallery_images")
        .select("display_order")
        .order("display_order", { ascending: false })
        .limit(1);

      const nextOrder = existing && existing.length > 0 ? existing[0].display_order + 1 : 0;

      // Insert into gallery_images table
      const { error: insertError } = await supabase
        .from("gallery_images")
        .insert({
          image_url: urlData.publicUrl,
          alt_text: altText,
          display_order: nextOrder,
        });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery-images"] });
      queryClient.invalidateQueries({ queryKey: ["admin-gallery-images"] });
      toast({ title: "Image added to gallery" });
    },
    onError: (error) => {
      toast({
        title: "Failed to add image",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateGalleryImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { id: string; altText?: string; displayOrder?: number; isActive?: boolean }) => {
      const { id, altText, displayOrder, isActive } = params;
      
      const updates: Partial<GalleryImage> = {};
      if (altText !== undefined) updates.alt_text = altText;
      if (displayOrder !== undefined) updates.display_order = displayOrder;
      if (isActive !== undefined) updates.is_active = isActive;

      const { error } = await supabase
        .from("gallery_images")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery-images"] });
      queryClient.invalidateQueries({ queryKey: ["admin-gallery-images"] });
      toast({ title: "Image updated" });
    },
    onError: (error) => {
      toast({
        title: "Failed to update image",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteGalleryImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: { id: string; imageUrl: string }) => {
      const { id, imageUrl } = params;
      
      // Extract filename from URL
      const urlParts = imageUrl.split("/");
      const fileName = urlParts[urlParts.length - 1];

      // Delete from storage
      await supabase.storage.from("gallery").remove([fileName]);

      // Delete from database
      const { error } = await supabase
        .from("gallery_images")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery-images"] });
      queryClient.invalidateQueries({ queryKey: ["admin-gallery-images"] });
      toast({ title: "Image deleted" });
    },
    onError: (error) => {
      toast({
        title: "Failed to delete image",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useReoptimizeGallery = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const mutation = useMutation({
    mutationFn: async (images: GalleryImage[]) => {
      // Filter to non-WebP images that likely need optimization
      const toOptimize = images.filter(
        (img) => !img.image_url.endsWith(".webp")
      );
      setProgress({ current: 0, total: toOptimize.length });

      let optimized = 0;

      for (const image of toOptimize) {
        try {
          // Download the original image
          const response = await fetch(image.image_url);
          if (!response.ok) continue;
          const blob = await response.blob();
          const originalFile = new File([blob], "original.jpg", {
            type: blob.type,
          });

          // Run through optimizer
          const optimizedFile = await optimizeImage(originalFile);

          // Upload optimized version with new filename
          const ext = optimizedFile.name.split(".").pop() || "webp";
          const newFileName = generateSafeFilename("gallery", ext);

          const { error: uploadError } = await supabase.storage
            .from("gallery")
            .upload(newFileName, optimizedFile);

          if (uploadError) continue;

          // Get new public URL
          const { data: urlData } = supabase.storage
            .from("gallery")
            .getPublicUrl(newFileName);

          // Update DB record
          await supabase
            .from("gallery_images")
            .update({ image_url: urlData.publicUrl })
            .eq("id", image.id);

          // Try to delete old file from storage
          const oldPath = image.image_url.split("/gallery/").pop();
          if (oldPath) {
            await supabase.storage.from("gallery").remove([oldPath]);
          }

          optimized++;
          setProgress({ current: optimized, total: toOptimize.length });
        } catch {
          // Skip failed images, continue with rest
          console.warn(`Failed to optimize image ${image.id}`);
        }
      }

      return optimized;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["gallery-images"] });
      queryClient.invalidateQueries({ queryKey: ["admin-gallery-images"] });
      toast({
        title: `Optimized ${count} image${count !== 1 ? "s" : ""}`,
        description: "Gallery images converted to WebP and compressed.",
      });
      setProgress({ current: 0, total: 0 });
    },
    onError: (error) => {
      toast({
        title: "Optimization failed",
        description: error.message,
        variant: "destructive",
      });
      setProgress({ current: 0, total: 0 });
    },
  });

  return { ...mutation, progress };
};
